// Create-case modal. Mirrors createCaseSchema field-for-field; validates client-side
// before POSTing so a bad payload never leaves the browser. Queueable: an offline
// submit parks in the queue and is treated as a success (it replays on reconnect).
import { useEffect, useState } from 'react';
import {
  type Case,
  type Clinic,
  type CreateCaseRequest,
  type CasePriority,
  type CaseType,
  type CaseArch,
  createCaseSchema,
  CASE_PRIORITIES,
  CASE_PRIORITY_LABELS,
  CASE_TYPES,
  CASE_TYPE_LABELS,
  CASE_ARCHES,
  CASE_ARCH_LABELS,
} from '@alignova/shared';
import { api, isDeferred } from '../../lib/api.ts';
import { t } from '../../i18n/index.ts';
import { Button, Card } from '../../ui/primitives.tsx';

const inputClass = 'w-full rounded border border-line bg-surface px-3 py-2 text-sm';

export function NewCaseModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [caseType, setCaseType] = useState<CaseType>('aligner');
  const [clinics, setClinics] = useState<Clinic[]>([]);
  const [clinicId, setClinicId] = useState('');
  const [clientName, setClientName] = useState('');
  const [service, setService] = useState('');
  const [patientCode, setPatientCode] = useState('');
  const [arch, setArch] = useState<CaseArch | ''>('');
  const [tooth, setTooth] = useState('');
  const [totalStagesUpper, setTotalStagesUpper] = useState('');
  const [totalStagesLower, setTotalStagesLower] = useState('');
  const [alignerMaterial, setAlignerMaterial] = useState('');
  const [priority, setPriority] = useState<CasePriority>('normal');
  const [dueDate, setDueDate] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Load the active clinics for the client picker whenever the modal opens.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    api
      .get<Clinic[]>('/clinics?active=true')
      .then((list) => {
        if (!cancelled) setClinics(list);
      })
      .catch(() => {
        if (!cancelled) setClinics([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  if (!open) return null;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    // A picked clinic supplies both the id and the denormalized name snapshot; the
    // house/walk-in option (clinicId === '') falls back to the free-text client name.
    const selectedClinic = clinicId ? clinics.find((c) => c.id === clinicId) : undefined;
    const resolvedClientName = selectedClinic ? selectedClinic.name : clientName;
    const isAligner = caseType === 'aligner';

    // Normalise empty optionals to undefined so nullish schema fields stay clean.
    const payload = {
      caseType,
      clientName: resolvedClientName,
      clientId: clinicId || undefined,
      service,
      patientCode: patientCode.trim() || undefined,
      arch: arch || undefined,
      tooth: tooth.trim() || undefined,
      totalStagesUpper: isAligner && totalStagesUpper !== '' ? Number(totalStagesUpper) : undefined,
      totalStagesLower: isAligner && totalStagesLower !== '' ? Number(totalStagesLower) : undefined,
      alignerMaterial: isAligner ? alignerMaterial.trim() || undefined : undefined,
      priority,
      dueDate: dueDate || undefined,
      notes: notes.trim() || undefined,
    };

    const parsed = createCaseSchema.safeParse(payload);
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? t('cases.form.invalid'));
      return;
    }

    const data: CreateCaseRequest = parsed.data;
    setSubmitting(true);
    try {
      const res = await api.post<Case>('/cases', data, { queue: true });
      // Deferred = queued offline; the write still lands on reconnect, so treat as done.
      if (isDeferred(res)) {
        onCreated();
        onClose();
        return;
      }
      onCreated();
      onClose();
    } catch {
      setError(t('cases.form.saveError'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-modal flex items-center justify-center bg-black/50 p-4">
      <Card className="max-h-screen w-full max-w-2xl overflow-y-auto p-6">
        <h2 className="mb-4 text-lg font-semibold text-ink">{t('cases.form.title')}</h2>
        <form className="space-y-4" onSubmit={submit}>
          <div className="md:grid md:grid-cols-[11rem_minmax(0,1fr)] md:items-start md:gap-4">
            <label className="mb-1 block text-sm text-muted md:mb-0 md:pt-2">{t('cases.form.caseType')}</label>
            <select
              className={inputClass}
              value={caseType}
              onChange={(e) => setCaseType(e.target.value as CaseType)}
            >
              {CASE_TYPES.map((ct) => (
                <option key={ct} value={ct}>
                  {CASE_TYPE_LABELS[ct]}
                </option>
              ))}
            </select>
          </div>
          <div className="md:grid md:grid-cols-[11rem_minmax(0,1fr)] md:items-start md:gap-4">
            <label className="mb-1 block text-sm text-muted md:mb-0 md:pt-2">{t('cases.form.clientName')}</label>
            <select
              className={inputClass}
              value={clinicId}
              onChange={(e) => setClinicId(e.target.value)}
            >
              <option value="">{t('cases.form.houseClient')}</option>
              {clinics.map((clinic) => (
                <option key={clinic.id} value={clinic.id}>
                  {`${clinic.clinicCode} - ${clinic.name}`}
                </option>
              ))}
            </select>
          </div>
          {!clinicId && (
            <div className="md:grid md:grid-cols-[11rem_minmax(0,1fr)] md:items-start md:gap-4">
              <label className="mb-1 block text-sm text-muted md:mb-0 md:pt-2">
                {t('cases.form.walkInName')}
              </label>
              <input
                className={inputClass}
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                required
              />
            </div>
          )}
          <div className="md:grid md:grid-cols-[11rem_minmax(0,1fr)] md:items-start md:gap-4">
            <label className="mb-1 block text-sm text-muted md:mb-0 md:pt-2">{t('cases.form.service')}</label>
            <input
              className={inputClass}
              value={service}
              onChange={(e) => setService(e.target.value)}
              required
            />
          </div>
          <div className="grid grid-cols-1 gap-x-4 gap-y-3">
            <div className="md:grid md:grid-cols-[11rem_minmax(0,1fr)] md:items-start md:gap-4">
              <label className="mb-1 block text-sm text-muted md:mb-0 md:pt-2">{t('cases.form.patientCode')}</label>
              <input
                className={inputClass}
                value={patientCode}
                onChange={(e) => setPatientCode(e.target.value)}
              />
            </div>
            <div className="md:grid md:grid-cols-[11rem_minmax(0,1fr)] md:items-start md:gap-4">
              <label className="mb-1 block text-sm text-muted md:mb-0 md:pt-2">
                {caseType === 'aligner' || caseType === 'retainer'
                  ? t('cases.form.arch')
                  : t('cases.form.tooth')}
              </label>
              {caseType === 'aligner' || caseType === 'retainer' ? (
                <select
                  className={inputClass}
                  value={arch}
                  onChange={(e) => setArch(e.target.value as CaseArch | '')}
                >
                  <option value="">{t('cases.form.archNone')}</option>
                  {CASE_ARCHES.map((a) => (
                    <option key={a} value={a}>
                      {CASE_ARCH_LABELS[a]}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  className={inputClass}
                  value={tooth}
                  onChange={(e) => setTooth(e.target.value)}
                />
              )}
            </div>
          </div>
          {caseType === 'aligner' && (
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="md:grid md:grid-cols-[11rem_minmax(0,1fr)] md:items-start md:gap-4">
                <label className="mb-1 block text-sm text-muted md:mb-0 md:pt-2">
                  {t('cases.form.totalStagesUpper')}
                </label>
                <input
                  type="number"
                  min={0}
                  className={inputClass}
                  value={totalStagesUpper}
                  onChange={(e) => setTotalStagesUpper(e.target.value)}
                />
              </div>
              <div className="md:grid md:grid-cols-[11rem_minmax(0,1fr)] md:items-start md:gap-4">
                <label className="mb-1 block text-sm text-muted md:mb-0 md:pt-2">
                  {t('cases.form.totalStagesLower')}
                </label>
                <input
                  type="number"
                  min={0}
                  className={inputClass}
                  value={totalStagesLower}
                  onChange={(e) => setTotalStagesLower(e.target.value)}
                />
              </div>
              <div className="md:grid md:grid-cols-[11rem_minmax(0,1fr)] md:items-start md:gap-4">
                <label className="mb-1 block text-sm text-muted md:mb-0 md:pt-2">
                  {t('cases.form.material')}
                </label>
                <input
                  className={inputClass}
                  value={alignerMaterial}
                  onChange={(e) => setAlignerMaterial(e.target.value)}
                />
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 gap-x-4 gap-y-3">
            <div className="md:grid md:grid-cols-[11rem_minmax(0,1fr)] md:items-start md:gap-4">
              <label className="mb-1 block text-sm text-muted md:mb-0 md:pt-2">{t('cases.form.priority')}</label>
              <select
                className={inputClass}
                value={priority}
                onChange={(e) => setPriority(e.target.value as CasePriority)}
              >
                {CASE_PRIORITIES.map((p) => (
                  <option key={p} value={p}>
                    {CASE_PRIORITY_LABELS[p]}
                  </option>
                ))}
              </select>
            </div>
            <div className="md:grid md:grid-cols-[11rem_minmax(0,1fr)] md:items-start md:gap-4">
              <label className="mb-1 block text-sm text-muted md:mb-0 md:pt-2">{t('cases.form.dueDate')}</label>
              <input
                type="date"
                className={inputClass}
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
              />
            </div>
          </div>
          <div className="md:grid md:grid-cols-[11rem_minmax(0,1fr)] md:items-start md:gap-4">
            <label className="mb-1 block text-sm text-muted md:mb-0 md:pt-2">{t('cases.form.notes')}</label>
            <textarea
              className={inputClass}
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          {error && <p className="text-xs text-status-urgent-fg">{error}</p>}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              {t('cases.form.cancel')}
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? t('cases.form.saving') : t('cases.form.submit')}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
